import { Link } from 'react-router-dom';

export interface HeroProps {
  headline: string;
  subheadline: string;
  primaryCTA: { label: string; href: string };
  secondaryCTA: { label: string; href: string };
  trustSignal: string;
  bgImage: string;
  badge?: string;
  floatingBadgeTitle?: string;
  floatingBadgeText?: string;
}

export default function HeroSection({
  headline,
  subheadline,
  primaryCTA,
  secondaryCTA,
  trustSignal,
  bgImage,
  badge,
  floatingBadgeTitle = '25+ Years Experience',
  floatingBadgeText = 'Premium flooring specialists in Brooklyn, NY',
}: HeroProps) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background image — bright, premium lighting */}
      <div className="absolute inset-0">
        <img
          src={bgImage}
          alt="Premium flooring showroom and installation"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-white/20"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-32 pt-40">
        <div className="max-w-2xl">
          {badge && (
            <div className="inline-flex items-center gap-2 bg-bronze/10 text-bronze text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-8">
              <span className="block w-1.5 h-1.5 bg-bronze rounded-full"></span>
              {badge}
            </div>
          )}

          <h1 className="font-serif text-5xl md:text-6xl font-semibold text-charcoal leading-tight mb-6">
            {headline}
          </h1>

          <p className="text-lg text-gray-600 leading-relaxed mb-10 max-w-xl">
            {subheadline}
          </p>

          <div className="flex flex-wrap gap-4 mb-10">
            <Link
              to={primaryCTA.href}
              className="bg-bronze text-white font-semibold px-8 py-4 rounded-md hover:bg-bronze-dark transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              {primaryCTA.label}
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
            <Link
              to={secondaryCTA.href}
              className="bg-white text-charcoal border-2 border-charcoal/20 font-semibold px-8 py-4 rounded-md hover:border-bronze hover:text-bronze transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {secondaryCTA.label}
            </Link>
          </div>

          {/* Trust signal */}
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {['bg-bronze/20', 'bg-sage/30', 'bg-bronze/10'].map((c, i) => (
                <div key={i} className={`w-9 h-9 rounded-full ${c} border-2 border-white flex items-center justify-center`}>
                  <i className="ri-user-line text-xs text-charcoal/60"></i>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <div key={s} className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-star-fill text-bronze text-xs"></i>
                  </div>
                ))}
              </div>
              <span className="text-sm font-medium text-gray-600">{trustSignal}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating card bottom right */}
      <div className="absolute bottom-12 right-8 hidden lg:block bg-white rounded-2xl p-5 max-w-xs border border-warmgray">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-bronze/10 rounded-full shrink-0">
            <i className="ri-store-3-line text-bronze text-lg"></i>
          </div>
          <div>
            <p className="text-xs font-bold text-charcoal mb-0.5">{floatingBadgeTitle}</p>
            <p className="text-xs text-gray-500">{floatingBadgeText}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
